import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Calendar, 
  DollarSign, 
  Heart, 
  MessageSquare, 
  Clock, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  FileText,
  Users
} from 'lucide-react';

export function Dashboard() {
  const quickActions = [
    { id: 'askhr', label: 'Ask HR Question', icon: MessageSquare, color: 'bg-blue-500' },
    { id: 'timeoff', label: 'Request Time Off', icon: Calendar, color: 'bg-green-500' },
    { id: 'payroll', label: 'View Paystub', icon: DollarSign, color: 'bg-purple-500' },
    { id: 'benefits', label: 'Check Benefits', icon: Heart, color: 'bg-red-500' },
  ];

  const recentActivities = [
    { id: 1, action: 'Submitted time off request', date: '2 hours ago', status: 'pending' },
    { id: 2, action: 'Completed harassment training', date: '1 day ago', status: 'completed' },
    { id: 3, action: 'Updated emergency contact', date: '3 days ago', status: 'completed' },
    { id: 4, action: 'Viewed benefits summary', date: '5 days ago', status: 'completed' },
  ];

  const announcements = [
    { 
      id: 1, 
      title: 'New Holiday Schedule Released', 
      summary: 'View the updated holiday schedule for 2024',
      date: '2 days ago',
      priority: 'high'
    },
    { 
      id: 2, 
      title: 'Employee Wellness Program Launch', 
      summary: 'Join our new wellness initiatives starting next month',
      date: '1 week ago',
      priority: 'medium'
    },
    { 
      id: 3, 
      title: 'System Maintenance Notice', 
      summary: 'Scheduled maintenance this weekend',
      date: '1 week ago',
      priority: 'low'
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6">
        <h1 className="text-2xl font-bold mb-2">Welcome back, Sarah!</h1>
        <p className="text-blue-100">
          Here's what's happening with your account today. Check out your recent activities and quick actions below.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">PTO Balance</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">18.5 days</div>
            <p className="text-xs text-muted-foreground">+2.1 from last month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Wellness Score</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">85%</div>
            <Progress value={85} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">Great progress!</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Training Progress</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">7/10</div>
            <Progress value={70} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">3 modules remaining</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Actions</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3</div>
            <p className="text-xs text-muted-foreground">2 urgent, 1 normal</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Frequently used services and tools</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <Button
                  key={action.id}
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-center gap-2"
                >
                  <div className={`${action.color} text-white p-3 rounded-full`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <span className="text-sm text-center">{action.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
            <CardDescription>Your recent actions and updates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {activity.status === 'completed' ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Clock className="h-5 w-5 text-yellow-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activity.action}</p>
                    <p className="text-xs text-muted-foreground">{activity.date}</p>
                  </div>
                  <Badge variant={activity.status === 'completed' ? 'secondary' : 'outline'}>
                    {activity.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card>
          <CardHeader>
            <CardTitle>Company Announcements</CardTitle>
            <CardDescription>Latest news and updates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div key={announcement.id} className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{announcement.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {announcement.summary}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {announcement.date}
                      </p>
                    </div>
                    <Badge 
                      variant={announcement.priority === 'high' ? 'destructive' : 
                              announcement.priority === 'medium' ? 'default' : 'secondary'}
                    >
                      {announcement.priority}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}